class NSSCarousel extends HTMLElement {

  constructor() {
    super();

    this.debug = false;

    this.swiper;
    this.settings;
    this.videoSlideEls = this.querySelectorAll(`[nss-video-playing]`);
  }

  connectedCallback() {
    if (this.debug) console.log("[NSS][Carousel] Connected.");

    const section = this.closest(`.shopify-section`);
    if (section) section.style.overflow = `hidden`;

    // Defer until after layout so offsetWidth is accurate for padSlides
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.padSlides();
        this.initCarousel();
      });
    });

    this.addEventListener(`click`, this.handleClick.bind(this));

    // Re-measure on resize so slide widths / centering stay correct after a
    // viewport or breakpoint change (debounced).
    this._onResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        if (this.swiper) {
          this.swiper.update();
          this.updateCentered();
        }
      }, 150);
    };
    window.addEventListener(`resize`, this._onResize);

    const editorEvents = [
      `shopify:inspector:activate`,
      `shopify:inspector:deactivate`,
      `shopify:section:load`,
      `shopify:section:unload`,
      `shopify:section:select`,
      `shopify:section:deselect`,
      `shopify:section:reorder`,
      `shopify:block:select`,
      `shopify:block:deselect`,
    ]
    editorEvents.forEach((event) => {
      this.addEventListener(event, this.handleEditorEvent.bind(this));
    });
  }

  disconnectedCallback() {
    if (this._onResize) window.removeEventListener(`resize`, this._onResize);
    clearTimeout(this._resizeTimer);
  }

  handleClick(e) {

    if (e.target.parentElement.hasAttribute(`nss-control-start`)) {
      this.swiper.slideTo(0);
    }

    if (e.target.parentElement.hasAttribute(`nss-control-end`)) {
      let count = this.swiper.slides.length;
      this.swiper.slideTo(count - 1);
    }

    if (e.target.hasAttribute(`nss-video-playing`)) {
      if (this.debug) console.log("[NSS][Carousel] Clicked video!");
      if (e.target.getAttribute(`nss-video-playing`) === "false") {
        if (this.debug) console.log("[NSS][Carousel] Playing video!");
        this.stopAllVideos();
        e.target.setAttribute(`nss-video-playing`, true);
        const videoEl = e.target.querySelector(`video`);
        videoEl.volume = 0.2;
        videoEl.play();
        if (typeof this.settings.autoplay !== `undefined`) {
          this.swiper.autoplay.stop();
        }
      } else {
        if (this.debug) console.log("[NSS][Carousel] Not playing video!");
        this.stopAllVideos();
        if (typeof this.settings.autoplay !== `undefined`) {
          this.swiper.autoplay.start();
        }
      }
    }
  }

  stopAllVideos() {
    this.videoSlideEls.forEach((videoSlideEl) => {
      const videoEl = videoSlideEl.querySelector(`video`);
      videoEl.pause();
      videoSlideEl.setAttribute(`nss-video-playing`, false);
    });
  }

  padSlides() {
    const wrapperEl = this.querySelector('.swiper-wrapper');
    if (!wrapperEl) return;

    const originalSlides = [...wrapperEl.children].filter(el => el.classList.contains('swiper-slide'));
    const slideCount = originalSlides.length;
    if (slideCount === 0) return;

    // Check if loop is enabled for the current breakpoint
    const settings = JSON.parse(this.querySelector('#swiper-settings').textContent);
    const width = window.innerWidth;

    let loopEnabled = !!settings.loop;
    if (settings.breakpoints) {
      const bps = Object.keys(settings.breakpoints).map(Number).sort((a, b) => a - b);
      for (const bp of bps) {
        if (width >= bp && typeof settings.breakpoints[bp].loop !== 'undefined') {
          loopEnabled = !!settings.breakpoints[bp].loop;
        }
      }
    }

    if (!loopEnabled) return;

    // Measure actual slide width and viewport to determine how many fit
    const slideWidth = originalSlides[0].offsetWidth;
    if (slideWidth === 0) return;

    const viewportWidth = width;
    const slidesVisible = Math.ceil(viewportWidth / slideWidth);

    // Need at least slidesVisible * 2 for a natural loop
    const minSlides = slidesVisible * 2;
    if (slideCount >= minSlides) return;

    if (this.debug) console.log(`[NSS][Carousel] Padding slides: ${slideCount} → ${minSlides} (viewport: ${viewportWidth}px, slide: ${slideWidth}px)`);

    // Clone full sets of original slides until we have enough
    while (wrapperEl.children.length < minSlides) {
      for (const slide of originalSlides) {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-cloned', 'true');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-block-id');
        wrapperEl.appendChild(clone);
      }
    }
  }

  initCarousel() {
    const carouselEl = this.querySelector(`.swiper`);
    const settings = JSON.parse(this.querySelector(`#swiper-settings`).textContent);

    // if (this.debug) console.log("[NSS][Carousel] settings:", settings);

    const presets = {
      a11y: {
        enabled: true,
      },
      navigation: {
        prevEl: '.nss-carousel-button-prev',
        nextEl: '.nss-carousel-button-next',
      },
      pagination: {
        el: '.swiper-pagination',
        type: 'bullets',
        clickable: true,
      },
      scrollbar: {
        el: '.swiper-scrollbar',
        draggable: true
      },
      grabCursor: true,
      // Carousels can initialize inside hidden containers (e.g. per-variant
      // nss-buybox-bottom wrappers on the PDP). The observer re-measures the
      // swiper when a parent's class/style changes (variant switch), so
      // sizes/centering are correct once it becomes visible.
      observer: true,
      observeParents: true,
    }

    this.settings = Object.assign(presets, settings);
    if (this.debug) console.log("[NSS][Carousel] this.settings:", this.settings);

    this.swiper = new Swiper(carouselEl, this.settings);

    this.swiper.on(`reachBeginning`, this.handleReachBeginning.bind(this));
    this.swiper.on(`reachEnd`, this.handleReachEnd.bind(this));
    this.swiper.on(`slideChange`, this.handleSlideChange.bind(this));

    // Centering is only safe when the slides fit (no scrolling). Toggle the
    // .is-centered class from Swiper's locked state, on init and whenever it
    // changes (lock/unlock fire on resize/breakpoint changes too).
    this.updateCentered();
    this.swiper.on(`lock`, this.updateCentered.bind(this));
    this.swiper.on(`unlock`, this.updateCentered.bind(this));
    this.swiper.on(`resize`, this.updateCentered.bind(this));
    // Fired by the observer when a hidden parent becomes visible
    this.swiper.on(`observerUpdate`, this.updateCentered.bind(this));
    this.swiper.on(`update`, this.updateCentered.bind(this));
  }

  updateCentered() {
    if (!this.hasAttribute(`nss-center-carousel`) || !this.swiper) return;
    // Hidden container (e.g. inactive variant wrapper): sizes are 0 and any
    // decision would be wrong — leave the current state, the observer will
    // re-run this when the carousel becomes visible.
    if (!this.swiper.size) return;
    // isLocked alone misses centeredSlides carousels — they always keep a
    // translate to center the active slide, so Swiper never "locks" even
    // when all slides fit. Also center whenever the slides physically fit
    // the container. virtualSize includes a trailing spaceBetween gap —
    // subtract it so a row that exactly fits still counts as fitting.
    const space = (this.swiper.params && this.swiper.params.spaceBetween) || 0;
    const fits = this.swiper.virtualSize - space <= this.swiper.size + 1;
    const centered = !!this.swiper.isLocked || fits;
    this.classList.toggle(`is-centered`, centered);
    // Neutralize the centeredSlides translate — otherwise the flex-centered
    // wrapper is still shifted by Swiper's inline transform.
    if (centered && this.swiper.translate !== 0) this.swiper.setTranslate(0);
    // Overflowing row (lots of cards): start on the middle slide so the
    // carousel still reads as centered instead of anchoring to the left.
    if (!centered) this.centerInitialSlide();
  }

  centerInitialSlide() {
    // Only for "Center carousel" + centeredSlides, once per page load; loop
    // mode manages its own indexes (cloned slides) so leave it alone.
    if (this._initialCentered || !this.swiper) return;
    if (!this.swiper.params.centeredSlides || this.swiper.params.loop) return;
    const count = this.swiper.slides.length;
    if (count < 2) return;
    this._initialCentered = true;
    this.swiper.slideTo(Math.floor((count - 1) / 2), 0);
  }

  handleSlideChange() {
    // const advisorEls = this.querySelectorAll(`nss-carousel-slide-advisor`);
    // advisorEls.forEach((advisorEl) => {
    //   advisorEl.parentElement.classList.remove(`nss-active`);
    // });

    // swiper.update() can itself emit `slideChange` (some settings — e.g. loop
    // with cloned/padded slides — shift the active index during update), which
    // would recurse infinitely. Guard so it runs once per real change.
    if (this._isUpdating) return;
    this._isUpdating = true;
    this.swiper.update();
    this._isUpdating = false;
  }

  handleReachBeginning() {
    if (this.debug) console.log(`[NSS][Carousel] Reached beginning!`);

    const controlsEl = this.querySelector(`nss-start-end-controls`);
    const startEl = this.querySelector(`[nss-control-start]`);
    const endEl = this.querySelector(`[nss-control-end]`);
    if (controlsEl) {
      startEl.classList.add(`nss-active`);
      endEl.classList.remove(`nss-active`);
      controlsEl.setAttribute(`nss-position`, `start`);
    }
  }

  handleReachEnd() {
    if (this.debug) console.log(`[NSS][Carousel] Reached end!`);

    const controlsEl = this.querySelector(`nss-start-end-controls`);
    const startEl = this.querySelector(`[nss-control-start]`);
    const endEl = this.querySelector(`[nss-control-end]`);
    if (controlsEl) {
      startEl.classList.remove(`nss-active`);
      endEl.classList.add(`nss-active`);
      controlsEl.setAttribute(`nss-position`, `end`);
    }
  }

  handleEditorEvent(e) {
    if (this.debug) console.log("[NSS][Carousel] Handle editor event, e:", e);

    switch(e.type) {

      case `shopify:block:select`:
        const blockEl = this.querySelector(`[data-block-id="${e.detail.blockId}"]`);
        const slideEl = blockEl.closest(`.swiper-slide`);
        if (this.debug) console.log("[NSS][Carousel] slideEl:", slideEl);
        if (slideEl) {
          // const index = slideEl.getAttribute(`data-swiper-slide-index`);
          const slideEls = [...this.querySelectorAll(`.swiper-slide`)];
          const index = slideEls.indexOf(slideEl);
          if (this.debug) console.log("[NSS][Carousel] Sliding to index:", index);
          this.swiper.slideTo(index);
        }
        break;

    }
  }
}

let elementName = `nss-carousel`;
if (typeof customElements.get(elementName) === `undefined`) {
  customElements.define(elementName, NSSCarousel);
} else {
  console.warn(`[NSS] Element '${elementName}' already defined.`)
}